import React from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Alert,
    Container,
} from "@mui/material";
import {
    Block as BlockIcon,
    Payment as PaymentIcon,
    ContactSupport as ContactSupportIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../app/hooks";

interface SubscriptionRequiredProps {
    message?: string;
    isAdmin?: boolean;
}

const SubscriptionRequired: React.FC<SubscriptionRequiredProps> = ({
    message,
    isAdmin: propIsAdmin,
}) => {
    const navigate = useNavigate();
    const user = useAppSelector((state) => state.auth.user);

    // Determine if user is admin from props or from user state
    const isAdmin = propIsAdmin ?? user?.role === "Admin";

    const defaultMessage = isAdmin
        ? "Your subscription is not active. Please activate or renew your subscription to continue using the platform."
        : "Your organization's subscription is not active. Please contact your administrator to renew the subscription.";

    return (
        <Container maxWidth="md">
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "80vh",
                }}
            >
                <Card sx={{ maxWidth: 600, width: "100%" }}>
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 80,
                                height: 80,
                                borderRadius: "50%",
                                bgcolor: "error.light",
                                mb: 3,
                            }}
                        >
                            <BlockIcon sx={{ fontSize: 48, color: "error.main" }} />
                        </Box>

                        <Typography variant="h4" gutterBottom fontWeight="bold">
                            Subscription Required
                        </Typography>

                        <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
                            {message || defaultMessage}
                        </Alert>

                        {isAdmin ? (
                            <>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    As an administrator, you can manage your subscription from the
                                    Subscription Dashboard.
                                </Typography>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    startIcon={<PaymentIcon />}
                                    onClick={() => navigate("/subscription")}
                                    sx={{ mt: 2 }}
                                >
                                    Go to Subscription Dashboard
                                </Button>
                            </>
                        ) : (
                            <>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    You don't have permission to manage subscriptions. Please reach
                                    out to your organization's administrator.
                                </Typography>

                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    startIcon={<ContactSupportIcon />}
                                    onClick={() => navigate("/profile")}
                                    sx={{ mt: 2 }}
                                >
                                    View My Profile
                                </Button>
                            </>
                        )}

                        <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
                            <Typography variant="caption" color="text.secondary">
                                If you believe this is an error, please contact support.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
};

export default SubscriptionRequired;

